import { StrictMode, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { routeTree } from './routeTree.gen';
import { ClerkProvider, useUser } from '@clerk/clerk-react';
import { trpc, trpcClient, queryClient } from './trpc';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
// Import i18n provider
import { LanguageProvider } from './i18n/LanguageProvider';
// Import i18n instance for configuration
import './i18n';
// Initialize in-house analytics SDK (side-effect import)
import './lib/analytics';
import { analytics } from './lib/analytics';

// Bridges Clerk auth state into the analytics SDK. Two responsibilities:
//   1. Identify (so every event ships with user_id once Clerk has loaded).
//   2. Fire `signup_completed` for users whose Clerk account was created in
//      the last few minutes and who haven't already been tracked this session.
//      The real signup UI is Clerk's hosted modal (<SignUpButton>), which
//      doesn't run any of our code — this is the only place we can hook in
//      from. SessionStorage dedupes so a refresh after signup doesn't re-fire.
function AnalyticsIdentity() {
  const { isLoaded, user } = useUser();
  useEffect(() => {
    if (!isLoaded) return;
    if (!user?.id) {
      void analytics.reset();
      return;
    }
    void analytics.identify(user.id);

    const SIGNUP_WINDOW_MS = 10 * 60 * 1000;
    const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : null;
    const dedupeKey = `tp_signup_tracked_${user.id}`;
    if (
      createdAt !== null &&
      Date.now() - createdAt < SIGNUP_WINDOW_MS &&
      !sessionStorage.getItem(dedupeKey)
    ) {
      const provider = user.externalAccounts?.[0]?.provider;
      analytics.track(
        'signup_completed',
        provider ? { method: 'social', provider } : { method: 'email' },
      );
      sessionStorage.setItem(dedupeKey, '1');
    }
  }, [isLoaded, user?.id]);
  return null;
}

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}


Sentry.init({
  // dsn: 'https://38e8ca9d44ea2d82939f39108d8fdf59@o4509045097299968.ingest.us.sentry.io/4509045126856704',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: true,
  environment: import.meta.env.MODE,
  // Disable debug mode to prevent console spam
  debug: false,
});

// Initialize PostHog
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host:
      import.meta.env.VITE_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: true,
    session_recording: {
      // @ts-expect-error PostHog types don't include 'enabled', but it works
      enabled:
        !import.meta.env.DEV && // Disable session recording in development
        import.meta.env.VITE_PUBLIC_POSTHOG_SESSION_RECORDING?.toLowerCase() === 'true',
    },
    loaded: (posthog) => {
      const checkAndDisableForAdmin = (user?: any) => {
        const currentUser = user ?? window.Clerk?.user;
        if (currentUser) {
          const isAdmin = ((currentUser as any).publicMetadata as { role?: string })?.role === 'admin';
          if (isAdmin) {
            console.log('Admin user detected - disabling PostHog session recording');
            posthog.stopSessionRecording?.();
            // Also disable for future sessions
            posthog.config.disable_session_recording = true;
          }
        }
      };
      
      // Check if Clerk is already available
      if (window.Clerk?.user) {
        checkAndDisableForAdmin(window.Clerk.user);
      }
      
      // Listen for Clerk events using DOM events
      const handleClerkUser = (event: any) => {
        checkAndDisableForAdmin(event.detail?.user || window.Clerk?.user);
      };
      
      window.addEventListener('clerk:user', handleClerkUser);
      window.addEventListener('clerk:loaded', () => {
        checkAndDisableForAdmin(window.Clerk?.user);
      });
      
      // Note: We intentionally don't re-enable recording for non-admins
      // to respect the original environment configuration. This means recording
      // stays disabled for the entire browser session once an admin has logged in.
    },
  });
} else if (import.meta.env.DEV) {
  console.warn('PostHog key not found - analytics disabled');
}

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <LanguageProvider>
        <ClerkProvider
          publishableKey={PUBLISHABLE_KEY}
          afterSignUpUrl='/dashboard'
          appearance={{
            elements: {
              formButtonPrimary: 'bg-blue-600 hover:bg-blue-700',
            },
          }}
          localization={{
            signUp: {
              start: {
                title: 'Create Your Account',
                subtitle:
                  "Welcome! You're just one step away from organizing your job search like a pro! Two-week free POWER trial starts instantly.",
              },
            },
          }}
        >
          <AnalyticsIdentity />
          <trpc.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
            </QueryClientProvider>
          </trpc.Provider>
        </ClerkProvider>
      </LanguageProvider>
    </StrictMode>
  );
}
